import { useMemo } from 'react'
import { countAnchorMatches } from './anchor'
import { validateTourDoc } from './tourSchema'
import { flattenTourSteps, type TourDoc } from './tourTypes'

/**
 * `?fg_tour_selfcheck=1` — the twenty-second confidence check before a live
 * call. No overlay, no engine: schema validation plus, for every step, how many
 * elements its anchor matches on the CURRENT page. Steps on other pages say so
 * rather than pretending; the walkthrough script is the cross-page truth.
 */
export function TourSelfCheck({ doc }: { doc: TourDoc }) {
  const report = useMemo(() => {
    const validation = validateTourDoc(doc)
    const page = window.location.pathname.split('/').filter(Boolean).pop() || ''
    const rows = flattenTourSteps(doc).map(({ step, index }) => {
      if (!step.anchor) return { index, id: step.id, note: 'modal — no anchor' }
      if (step.route.page !== page) return { index, id: step.id, note: `on /${step.route.page} — run the walkthrough` }
      const n = countAnchorMatches(step.anchor)
      return { index, id: step.id, note: n === 1 ? 'ok — 1 match' : n === 0 ? 'MISSING — 0 matches' : `AMBIGUOUS — ${n} matches` }
    })
    return { validation, rows }
  }, [doc])

  return (
    <div style={{
      position: 'fixed', right: 16, bottom: 16, zIndex: 2147483001, width: 340,
      background: 'var(--fg-surface)', border: '1px solid var(--fg-border)',
      borderRadius: 'var(--fg-radius-md)', boxShadow: 'var(--fg-shadow-md)',
      padding: 14, fontFamily: 'var(--fg-font-ui)', fontSize: 12, color: 'var(--fg-text)',
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        Tour self-check · {doc.slug} · {report.validation.ok ? 'schema ok' : `${report.validation.errors.length} schema error(s)`}
      </div>
      {report.validation.errors.slice(0, 3).map((e, i) => (
        <div key={i} style={{ color: 'var(--fg-danger)' }}>{e.message}</div>
      ))}
      <div style={{ display: 'grid', gap: 2, marginTop: 6 }}>
        {report.rows.map((r) => (
          <div key={r.id} style={{ color: /MISSING|AMBIGUOUS/.test(r.note) ? 'var(--fg-danger)' : 'var(--fg-text-2)' }}>
            {r.index + 1}. {r.id} — {r.note}
          </div>
        ))}
      </div>
    </div>
  )
}
